import { ConfirmModal, useModalContext } from '@fourthwall/components';
import { useFormik } from '@fourthwall/utils/dist/formik';
import { useTagsQuery } from '@modules/Dashboard/Memberships/hooks/useTagsQuery';
import { usePostsQuery } from '@modules/Dashboard/Memberships/modules/Posts/hooks/usePostsQuery';
import { routing } from '@utils/routing';
import flatMap from 'lodash/flatMap';
import React, { createContext, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { validationSchema } from '../consts';
import { useDeletePodcastMutation } from '../queries/useDeletePodcastMutation';
import { useDesyncPodcastMutation } from '../queries/useDesyncPodcastMutation';
import { usePodcastQuery } from '../queries/usePodcastQuery';
import { usePodcastStatusCheckQuery } from '../queries/usePodcastStatusCheckQuery';
import { useSyncPodcastMutation } from '../queries/useSyncPodcastMutation';
import { useUpdatePodcastMutation } from '../queries/useUpdatePodcastMutation';
import { useUpdatePodcastTagsMutation } from '../queries/useUpdatePodcastTagsMutation';
import { getCategoryAndSubcategoryFromSelectedCategory } from '../utils';
export const PodcastContext = createContext(undefined);
export const PodcastProvider = ({ children }) => {
    const modal = useModalContext();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const { podcastId } = useParams();
    const navigate = useNavigate();
    const { podcastQuery } = usePodcastQuery([
        {
            id: Number(podcastId),
        },
    ]);
    const { postsQuery } = usePostsQuery([
        {
            page: 1,
            limit: 1,
            'types[]': ['Audio'],
            state: 'Published',
        },
    ]);
    usePodcastStatusCheckQuery([
        {
            id: Number(podcastId),
        },
    ], {
        enabled: podcastQuery.data?.status === 'Syncing',
        refetchInterval: (data) => (data?.status === 'Syncing' ? 3000000 : false),
        onSuccess: (data) => {
            if (data.status !== 'Syncing') {
                podcastQuery.refetch();
            }
        },
    });
    const { updatePodcastTagsMutation } = useUpdatePodcastTagsMutation();
    const { updatePodcastMutation } = useUpdatePodcastMutation();
    const { syncPodcastMutation } = useSyncPodcastMutation();
    const { desyncPodcastMutation } = useDesyncPodcastMutation();
    const { deletePodcastMutation } = useDeletePodcastMutation();
    const { tagsQuery } = useTagsQuery([{ limit: 9999 }]);
    const form = useFormik({
        initialValues: {
            withTags: podcastQuery.data?.tags ? podcastQuery.data.tags.length > 0 : false,
            tags: podcastQuery.data?.tags || [],
        },
        onSubmit: (values) => {
            if (!values.withTags) {
                return handleUpdateTags([]);
            }
            return handleUpdateTags(values.tags);
        },
        validationSchema,
        enableReinitialize: true,
    });
    const handleUpdateTags = useCallback(async (tags) => {
        await updatePodcastTagsMutation.mutateAsync([{ podcast_id: Number(podcastId) }, { tags }], {
            onSuccess: podcastQuery.refetch,
        });
    }, [updatePodcastTagsMutation, podcastId, podcastQuery]);
    const selectedTags = useMemo(() => {
        return (tagsQuery.data?.tags
            .filter((tag) => {
            return flatMap(form.values.tags || [], (tag) => tag.id).includes(tag.id);
        })
            .map((tag) => tag.name) || []);
    }, [form.values.tags, tagsQuery.data]);
    const handleAddTag = useCallback((newTag) => {
        const tag = tagsQuery.data?.tags.find((tag) => tag.name === newTag);
        if (!tag)
            return;
        form.setFieldValue('tags', [...form.values.tags, { id: tag.id }]);
    }, [form, tagsQuery]);
    const handleRemoveTag = useCallback((oldTag) => {
        const tag = tagsQuery.data?.tags.find((tag) => tag.name === oldTag);
        if (!tag)
            return;
        form.setFieldValue('tags', form.values.tags.filter((selectedTag) => selectedTag.id !== tag.id));
    }, [form, tagsQuery]);
    const handleUpdatePodcast = useCallback((values, newImageResourceId) => {
        const { category, subcategory } = getCategoryAndSubcategoryFromSelectedCategory(values.category);
        return updatePodcastMutation.mutateAsync([
            { id: Number(podcastId) },
            {
                name: values.title,
                description: values.description,
                creatorName: values.authorName,
                creatorEmail: values.contactEmail,
                category,
                subcategory: subcategory || undefined,
                newImageResourceId,
            },
        ], {
            onSuccess: () => podcastQuery.refetch().then(() => setIsEditModalOpen(false)),
        });
    }, [podcastId, updatePodcastMutation, podcastQuery]);
    const handleSyncPodcast = useCallback(() => {
        if (form.dirty) {
            return modal.open(ConfirmModal, {
                title: 'Unsaved changes',
                text: 'You have unsaved changes, please click Save or discard changes before syncing your podcast.',
            });
        }
        syncPodcastMutation.mutateAsync([{ id: Number(podcastId) }], {
            onSuccess: () => podcastQuery.refetch(),
        });
    }, [podcastId, syncPodcastMutation, podcastQuery]);
    const handleDesyncPodcast = useCallback(() => {
        desyncPodcastMutation.mutateAsync([{ id: Number(podcastId) }], {
            onSuccess: () => podcastQuery.refetch(),
        });
    }, [podcastId, desyncPodcastMutation, podcastQuery]);
    const handleRemovePodcast = useCallback(() => {
        if (!podcastQuery.data)
            return;
        modal.open(ConfirmModal, {
            title: 'Are you sure you want to remove this podcast?',
            text: podcastQuery.data.status === 'Synced'
                ? 'This action will also remove your podcast from Spotify.'
                : undefined,
            confirmLabel: 'Remove',
            confirmAppearance: 'destructive',
            onConfirm: () => deletePodcastMutation.mutateAsync([{ id: Number(podcastId) }], {
                onSuccess: () => {
                    navigate(routing.apps.spotify.self);
                },
            }),
        });
    }, [modal, podcastId, deletePodcastMutation, podcastQuery, navigate]);
    const value = useMemo(() => ({
        podcast: podcastQuery.data,
        hasAnyAudioPost: postsQuery.data ? postsQuery.data.posts.length === 1 : undefined,
        tags: tagsQuery.data?.tags.map((tag) => tag.name) || [],
        selectedTags,
        form,
        isLoading: podcastQuery.isFetching ||
            podcastQuery.isLoading ||
            tagsQuery.isLoading ||
            updatePodcastMutation.isLoading ||
            updatePodcastTagsMutation.isLoading ||
            syncPodcastMutation.isLoading ||
            desyncPodcastMutation.isLoading,
        isEditModalOpen,
        setIsEditModalOpen,
        handleAddTag,
        handleRemoveTag,
        handleUpdatePodcast,
        handleSyncPodcast,
        handleDesyncPodcast,
        handleRemovePodcast,
    }), [
        podcastQuery,
        postsQuery,
        tagsQuery,
        selectedTags,
        form,
        updatePodcastMutation,
        updatePodcastTagsMutation,
        isEditModalOpen,
        syncPodcastMutation,
        desyncPodcastMutation,
        handleAddTag,
        handleRemoveTag,
        handleUpdatePodcast,
        handleSyncPodcast,
        handleDesyncPodcast,
        handleRemovePodcast,
    ]);
    return React.createElement(PodcastContext.Provider, { value: value }, children);
};
